import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTerminal } from "@/lib/terminal";
import { errMsg } from "@/lib/api";
import { eth } from "@/lib/format";
import { SettingsCard } from "@/components/SettingsCard";
import { BulkAddCard } from "@/components/BulkAddCard";
import { WalletCard } from "@/components/WalletCard";

const Positions = () => {
  const { api, sell, busy, settings } = useTerminal();
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { setItems(await api("get", "/wallet/holdings")); } catch {} }, [api]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  return (
    <div className="rounded-md border border-[#222634] bg-[#101216] overflow-hidden" data-testid="holdings-table">
      <h2 className="px-3 h-10 flex items-center text-sm font-semibold border-b border-[#222634]">Açık Pozisyonlar (bu siteden alınanlar · PnL)</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="text-slate-500"><tr className="border-b border-[#222634]"><th className="text-left px-3 py-2">Token</th><th className="text-left px-2">Cüzdan</th><th className="text-right px-2">Maliyet</th><th className="text-right px-2">Değer</th><th className="text-right px-2">PnL</th><th className="text-right px-3">Sat</th></tr></thead>
          <tbody>
            {items.map((h) => (
              <tr key={`${h.token}-${h.wallet}`} className="border-b border-[#222634]/50 hover:bg-[#161920]" data-testid={`holding-row-${h.token}-${h.wallet_id}`}>
                <td className="px-3 py-2"><Link to={`/token/${h.token}`} className="flex items-center gap-2 hover:text-[#00E676]">{h.image && <img src={h.image} alt="" className="w-6 h-6 rounded" />}<span className="font-sans font-semibold">{h.symbol}</span>{h.source === "bot" && <span className="px-1 rounded bg-[#00F0FF]/10 text-[#00F0FF] text-[9px]">BOT</span>}</Link></td>
                <td className="px-2 text-slate-400">{h.wallet_label}</td>
                <td className="text-right px-2">{eth(h.cost_eth)}</td>
                <td className="text-right px-2">{eth(h.value_eth)}</td>
                <td className={`text-right px-2 ${h.pnl_pct >= 0 ? "text-[#00E676]" : "text-[#FF5252]"}`}>{h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct.toFixed(1)}%</td>
                <td className="px-3"><div className="flex justify-end gap-1">
                  {settings.sell_presets.map((p) => (
                    <button key={p} disabled={busy[`sell-${h.token}-${h.wallet_id}`]} data-testid={`holding-sell-${p}-${h.token}-${h.wallet_id}`} onClick={async () => { if (await sell(h.token, p, h.symbol, h.wallet_id)) load(); }}
                      className="px-2 py-1 rounded border border-[#FF5252]/30 text-[#FF5252] hover:bg-[#FF5252]/15 transition-colors disabled:opacity-40">%{p}</button>
                  ))}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-4 text-sm text-slate-500">Açık pozisyon yok</p>}
      </div>
    </div>
  );
};

export default function WalletsPage() {
  const { api, refreshWallet } = useTerminal();
  const [wallets, setWallets] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const load = useCallback(async () => {
    try { setWallets(await api("get", "/wallets")); } catch { setWallets([]); }
    refreshWallet();
  }, [api, refreshWallet]);
  useEffect(() => { load(); }, [load]);

  const claimAll = async () => {
    setClaiming(true);
    const id = toast.loading("Tüm cüzdanlarda claim taranıyor…");
    try {
      const r = await api("post", "/wallets/claim-all");
      if (!r.done.length && !r.failed.length) toast("Claim edilecek ödül yok", { id });
      else toast.success(`${r.done.length} claim yapıldı`, { id, description: r.failed.map((f) => `${f.wallet} ${f.symbol}: ${f.error}`).join(" | ") || undefined });
      load();
    } catch (e) { toast.error(errMsg(e), { id }); }
    finally { setClaiming(false); }
  };

  const total = (wallets || []).reduce((a, w) => a + Number(w.eth_balance), 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      <div className="lg:col-span-4 space-y-3">
        <BulkAddCard onSaved={load} />
        <SettingsCard />
      </div>
      <div className="lg:col-span-8 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold tracking-tight">Cüzdanlar</h1>
          <span className="font-mono text-sm text-slate-400" data-testid="wallets-total-balance">{(wallets || []).length} cüzdan · {total.toFixed(5)} Ξ</span>
          <button data-testid="claim-all-button" disabled={claiming || !wallets?.length} onClick={claimAll}
            className="ml-auto flex items-center gap-1.5 px-3 h-9 rounded border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 text-sm transition-colors disabled:opacity-40">
            {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}Tümünü Claim Et
          </button>
        </div>
        <p className="text-xs text-slate-500">Trade açık cüzdanlar tek tık alımlarda birlikte alır. Bot açık cüzdanlar sniper botta her yeni coinden alır. Cüzdana tıklayınca içindeki tüm full.fun tokenleri, ödülleri ve creator fee'leri görünür.</p>
        {!wallets && <p className="text-slate-500 text-sm">Yükleniyor…</p>}
        {wallets?.length === 0 && <p className="p-6 rounded border border-dashed border-[#222634] text-slate-500 text-sm" data-testid="wallets-empty">Henüz cüzdan yok. Soldan private key ekle.</p>}
        {wallets?.map((w) => <WalletCard key={w.id} w={w} onChange={load} />)}
        <Positions />
      </div>
    </div>
  );
}
